import React from "react";
import { Link } from "react-router-dom";
import { ASSETS } from "@/data/mockData";
import { Container, Section, Overline } from "@/components/ui-blocks";
import { Award, BookOpen, PenLine } from "lucide-react";
import Seo from "@/components/Seo";

export default function Founder() {
  return (
    <div data-testid="founder-page">
      <Seo title="Founder — Chantal Sajan" description="Meet Chantal Sajan, founder of Ace Asia Productions — five decades of writing about South-east Asian heritage." />
      {/* Editorial hero with founder image */}
      <section className="relative bg-[#281a12] text-[#FDFBF7] overflow-hidden grain" data-testid="founder-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-6">
            <Overline className="text-[#946f36] mb-6">
              <span className="bg-[#946f36]" /> Meet the Founder & Author
            </Overline>
            <h1 className="font-serif-display text-5xl md:text-7xl font-light leading-[1.02] tracking-tight aa-fade-up aa-delay-1">
              Chantal Sajan
            </h1>
            <p className="mt-4 text-[#946f36] font-serif-display italic text-2xl aa-fade-up aa-delay-2">
              Veteran journalist · Independent researcher
            </p>
            <p className="mt-6 text-[#D1C3B4] text-lg leading-relaxed max-w-xl aa-fade-up aa-delay-3">
              More than five decades of work in South-east Asian history,
              culture, and visual storytelling.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 aa-fade-up aa-delay-4">
              <Link
                to="/featured-book"
                data-testid="founder-featured-book-link"
                className="bg-[#946f36] hover:bg-[#FDFBF7] hover:text-[#281a12] text-[#281a12] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                View Featured Book
              </Link>
              <Link
                to="/multimedia"
                data-testid="founder-multimedia-link"
                className="border border-[#946f36] text-[#946f36] hover:bg-[#946f36] hover:text-[#281a12] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Listen to the Podcast
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-5 border border-[#946f36]/40 pointer-events-none" />
              <img
                src={ASSETS.founderPortrait}
                alt="Portrait of Chantal Sajan"
                data-testid="founder-portrait-image"
                className="relative w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bio */}
      <Section className="bg-[#FDFBF7]">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <img
                src={ASSETS.founderEditorial}
                alt="Chantal Sajan in batik kebaya"
                data-testid="founder-editorial-image"
                className="w-full aspect-[3/4] object-cover"
              />
            </div>
            <div className="lg:col-span-7">
              <Overline>The Story</Overline>
              <h2 className="mt-4 font-serif-display text-4xl md:text-5xl font-light leading-tight">
                Five decades of writing about the region.
              </h2>
              <div className="mt-6 space-y-5 text-[#4A3728] text-lg leading-relaxed">
                <p>
                  Chantal Sajan is a veteran Straits Times journalist and
                  independent researcher of South-east Asian history and
                  culture with more than five decades of experience.
                </p>
                <p>
                  Her first paperback book, <em>The Chettis of South-east
                  Asia</em>, brings together years of research, historical
                  evidence, archaeology and new scholarship to reveal a richer
                  understanding of the Chetti community and its place in
                  regional and global history.
                </p>
              </div>

              <div className="mt-10 grid sm:grid-cols-3 gap-5">
                <CredCard icon={PenLine} label="Senior Correspondent" sub="The Straits Times" />
                <CredCard icon={Award} label="US Society for News Design" sub="Award of Excellence, 1990" />
                <CredCard icon={BookOpen} label="Logo & Design" sub="SINDA · SAWL · SBC (MediaCorp)" />
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Pull quote */}
      <Section className="bg-[#F4F1EA]">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <span className="font-serif-display text-7xl text-[#6e5022]/40 leading-none">&ldquo;</span>
            <blockquote className="font-serif-display text-3xl md:text-4xl italic text-[#281a12] leading-snug" data-testid="founder-quote">
              The Chettis were not a footnote to the region&apos;s history. They
              were one of its compasses.
            </blockquote>
            <div className="mt-6 overline text-[#946f36]">— Chantal Sajan</div>
          </div>
        </Container>
      </Section>
    </div>
  );
}

function CredCard({ icon: Icon, label, sub }) {
  return (
    <div className="border border-[#6e5022]/25 p-5">
      <Icon className="w-5 h-5 text-[#6e5022]" strokeWidth={1.5} />
      <div className="mt-3 font-serif-display text-xl text-[#281a12]">{label}</div>
      <div className="text-xs text-[#4A3728] mt-1">{sub}</div>
    </div>
  );
}
